import React, { useState, useEffect, useMemo } from 'react';
import { Quote, AppSettings } from '../types';
import { 
  addQuote, 
  deleteQuote, 
  loginAdmin, 
  resetToDefaultQuotes, 
  updateSetting, 
  clearAdminToken,
  toggleQuoteActive,
  assignQuoteDate,
  updateQuote
} from '../lib/api';
import { 
  calculateDayStats, 
  formatToDateInputString, 
  getFriendlyDateLabel 
} from '../lib/dateUtils';

interface AdminPageProps {
  quotes: Quote[];
  settings: AppSettings;
  isD1: boolean;
  isAdminLoggedIn: boolean;
  onLoginSuccess: () => void;
  onLogout: () => void;
  onQuotesUpdated: (newQuotes: Quote[]) => void;
  onSettingsUpdated: (newSettings: AppSettings) => void;
  onNavigateHome: () => void;
  showToast: (msg: string) => void;
}

const AUTHOR_SUGGESTIONS = [
  'Marcus Aurelius',
  'Seneca',
  'Epictetus',
  'Friedrich Nietzsche',
  'Muhammad Ali',
  'Confucius',
  'Aristotle',
  'John C. Maxwell'
];

const CATEGORY_OPTIONS = [
  'Discipline',
  'Stoicism',
  'Focus',
  'Purpose',
  'Perseverance',
  'Mindset',
  'Habits',
  'General'
];

export const AdminPage: React.FC<AdminPageProps> = ({
  quotes,
  settings,
  isD1,
  isAdminLoggedIn,
  onLoginSuccess,
  onLogout,
  onQuotesUpdated,
  onSettingsUpdated,
  onNavigateHome,
  showToast
}) => {
  // Authentication states
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Active navigation tab: 'list' | 'schedule' | 'add' | 'settings'
  const [activeTab, setActiveTab] = useState<'list' | 'schedule' | 'add' | 'settings'>('list');

  // Filter & Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled' | 'scheduled'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<string>('');

  // Add Quote Form states
  const [newText, setNewText] = useState('');
  const [newAuthor, setNewAuthor] = useState('');
  const [newCategory, setNewCategory] = useState('Discipline');
  const [newAssignedDate, setNewAssignedDate] = useState('');
  const [newIsActive, setNewIsActive] = useState(true);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);

  // Settings tab states
  const [sinceDateInput, setSinceDateInput] = useState(settings.since_date || '2026-09-22');
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // Quick Date Assignment Modal state
  const [dateModalQuote, setDateModalQuote] = useState<Quote | null>(null);
  const [dateModalInput, setDateModalInput] = useState('');

  // Full Edit Quote Modal state
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [editText, setEditText] = useState('');
  const [editAuthor, setEditAuthor] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editAssignedDate, setEditAssignedDate] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Sync sinceDate
  useEffect(() => {
    setSinceDateInput(settings.since_date || '2026-09-22');
  }, [settings.since_date]);

  const todayString = useMemo(() => formatToDateInputString(new Date()), []);
  const tomorrowString = useMemo(() => {
    const t = new Date();
    t.setDate(t.getDate() + 1);
    return formatToDateInputString(t);
  }, []);

  // Calculate live preview stats
  const previewStats = calculateDayStats(sinceDateInput, null);

  // Metrics summary
  const totalCount = quotes.length;
  const activeCount = quotes.filter((q) => q.is_active !== 0).length;
  const disabledCount = quotes.filter((q) => q.is_active === 0).length;
  const scheduledCount = quotes.filter((q) => !!q.assigned_date).length;

  // Quotes assigned for today
  const todayQuotes = useMemo(() => {
    return quotes.filter((q) => q.assigned_date === todayString);
  }, [quotes, todayString]);

  // Quotes assigned to any date (ordered chronologically)
  const allScheduledQuotes = useMemo(() => {
    return quotes
      .filter((q) => !!q.assigned_date)
      .sort((a, b) => (a.assigned_date || '').localeCompare(b.assigned_date || ''));
  }, [quotes]);

  // Filtered quotes for the library list
  const filteredQuotes = useMemo(() => {
    return quotes.filter((q) => {
      // Search text, author, category
      const qText = q.text.toLowerCase();
      const qAuthor = q.author.toLowerCase();
      const qCat = q.category.toLowerCase();
      const search = searchQuery.toLowerCase().trim();
      const matchesSearch = !search || qText.includes(search) || qAuthor.includes(search) || qCat.includes(search);

      // Status filter
      let matchesStatus = true;
      if (statusFilter === 'active') {
        matchesStatus = q.is_active !== 0;
      } else if (statusFilter === 'disabled') {
        matchesStatus = q.is_active === 0;
      } else if (statusFilter === 'scheduled') {
        matchesStatus = !!q.assigned_date;
      }

      // Category filter
      const matchesCategory = categoryFilter === 'all' || q.category.toLowerCase() === categoryFilter.toLowerCase();

      // Date filter
      const matchesDate = !filterDate || q.assigned_date === filterDate;

      return matchesSearch && matchesStatus && matchesCategory && matchesDate;
    });
  }, [quotes, searchQuery, statusFilter, categoryFilter, filterDate]);

  // Distinct categories in existing quotes
  const existingCategories = useMemo(() => {
    const set = new Set<string>();
    CATEGORY_OPTIONS.forEach((c) => set.add(c));
    quotes.forEach((q) => {
      if (q.category) set.add(q.category);
    });
    return Array.from(set);
  }, [quotes]);

  // Handle Login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setLoginError('Password is required');
      return;
    }
    setLoginError('');
    setIsLoggingIn(true);
    const success = await loginAdmin(password);
    setIsLoggingIn(false);
    if (success) {
      setPassword('');
      onLoginSuccess();
      showToast('Admin access granted');
    } else {
      setLoginError('Access denied: Invalid admin passcode');
    }
  };

  // Handle Logout
  const handleLogoutClick = () => {
    clearAdminToken();
    onLogout();
    showToast('Logged out of Admin Console');
  };

  // Toggle active/disabled
  const handleToggleActive = async (id: string, currentStatus: number) => {
    const willBeActive = currentStatus === 0;
    // Optimistic UI update
    const updated = quotes.map((q) => (q.id === id ? { ...q, is_active: willBeActive ? 1 : 0 } : q));
    onQuotesUpdated(updated);

    try {
      await toggleQuoteActive(id, willBeActive);
      showToast(willBeActive ? 'Quote enabled & visible' : 'Quote disabled & hidden');
    } catch {
      showToast('Failed to update quote status');
      // Revert if error
      onQuotesUpdated(quotes);
    }
  };

  // Open Quick Date modal
  const openDateModal = (quote: Quote) => {
    setDateModalQuote(quote);
    setDateModalInput(quote.assigned_date || todayString);
  };

  // Save Quick Date (Enable for date)
  const handleSaveDateModal = async () => {
    if (!dateModalQuote) return;
    const targetDate = dateModalInput.trim() || null;

    // Optimistic UI update: auto-activate quote when assigning date
    const updated = quotes.map((q) =>
      q.id === dateModalQuote.id ? { ...q, assigned_date: targetDate, is_active: 1 } : q
    );
    onQuotesUpdated(updated);
    const currentId = dateModalQuote.id;
    setDateModalQuote(null);

    try {
      await assignQuoteDate(currentId, targetDate);
      if (targetDate) {
        showToast(`Quote enabled & scheduled for ${getFriendlyDateLabel(targetDate)} (${targetDate})`);
      } else {
        showToast('Quote moved to general rotation pool');
      }
    } catch {
      showToast('Failed to schedule quote');
      onQuotesUpdated(quotes);
    }
  };

  // Open Edit Modal
  const openEditModal = (quote: Quote) => {
    setEditingQuote(quote);
    setEditText(quote.text);
    setEditAuthor(quote.author);
    setEditCategory(quote.category);
    setEditAssignedDate(quote.assigned_date || '');
    setEditIsActive(quote.is_active !== 0);
  };

  // Save Edit Modal
  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingQuote || !editText.trim()) return;

    setIsSavingEdit(true);
    const updates: Partial<Quote> = {
      text: editText.trim(),
      author: editAuthor.trim() || 'Anonymous',
      category: editCategory.trim() || 'General',
      assigned_date: editAssignedDate.trim() ? editAssignedDate.trim() : null,
      is_active: editIsActive ? 1 : 0
    };

    // Optimistic update
    const updated = quotes.map((q) => (q.id === editingQuote.id ? { ...q, ...updates } : q));
    onQuotesUpdated(updated);

    try {
      await updateQuote(editingQuote.id, updates);
      showToast('Quote updated successfully');
      setEditingQuote(null);
    } catch {
      showToast('Failed to save updates');
      onQuotesUpdated(quotes);
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Create Quote
  const handleCreateQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newText.trim()) return;

    setIsSubmittingQuote(true);
    try {
      const created = await addQuote({
        text: newText.trim(),
        author: newAuthor.trim() || 'Anonymous',
        category: newCategory.trim() || 'Discipline',
        assigned_date: newAssignedDate.trim() ? newAssignedDate.trim() : null,
        is_active: newIsActive ? 1 : 0
      });
      onQuotesUpdated([created, ...quotes]);
      setNewText('');
      setNewAuthor('');
      setNewAssignedDate('');
      setNewIsActive(true);
      showToast('Quote saved to database');
      setActiveTab('list');
    } catch {
      showToast('Failed to save quote. Please re-authenticate.');
    } finally {
      setIsSubmittingQuote(false);
    }
  };

  // Delete Quote
  const handleDeleteQuote = async (id: string) => {
    if (!confirm('Are you sure you want to permanently delete this quote?')) return;
    try {
      await deleteQuote(id);
      onQuotesUpdated(quotes.filter((q) => q.id !== id));
      showToast('Quote deleted');
    } catch {
      showToast('Failed to delete quote');
    }
  };

  // Save Since Date Config
  const handleSaveSinceDate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sinceDateInput) return;
    setIsSavingConfig(true);
    try {
      await updateSetting('since_date', sinceDateInput);
      onSettingsUpdated({ ...settings, since_date: sinceDateInput });
      showToast(`Anchor date saved: ${sinceDateInput}`);
    } catch {
      showToast('Failed to save date. Please re-authenticate.');
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Restore Seeds
  const handleResetDefaults = async () => {
    if (!confirm('Reset quotes to defaults? This will restore initial seed quotes.')) return;
    try {
      const restored = await resetToDefaultQuotes();
      onQuotesUpdated(restored);
      showToast('Default quotes restored');
    } catch {
      showToast('Failed to reset quotes');
    }
  };

  // QUICK METRIC CHIP CLICK
  const handleMetricClick = (filter: 'all' | 'active' | 'disabled' | 'scheduled') => {
    setStatusFilter(filter);
    setActiveTab('list');
  };

  return (
    <div className="min-h-screen min-h-[100dvh] w-full bg-black text-white p-3 sm:p-6 md:p-8 flex flex-col items-center">
      <div className="w-full max-w-4xl space-y-6">

        {/* TOP BAR / NAVIGATION */}
        <header className="flex items-center justify-between pb-4 border-b border-zinc-800/80">
          <button
            onClick={onNavigateHome}
            className="flex items-center gap-2 px-3 py-2 -ml-2 rounded-xl text-xs font-mono text-zinc-400 hover:text-white hover:bg-zinc-900 transition-all active:scale-95"
            title="Return to public day counter"
          >
            <span className="text-sm">←</span>
            <span className="font-semibold">Day Counter</span>
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-zinc-900/90 border border-zinc-800 text-[11px] font-mono">
              <span className={`w-2 h-2 rounded-full ${isD1 ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
              <span className="text-zinc-400 hidden sm:inline uppercase">
                {isD1 ? 'Cloudflare D1' : 'Local Mode'}
              </span>
              <span className="text-zinc-400 sm:hidden uppercase">
                {isD1 ? 'D1' : 'Local'}
              </span>
            </div>

            {isAdminLoggedIn && (
              <button
                onClick={handleLogoutClick}
                className="px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-rose-950/40 border border-zinc-800 hover:border-rose-900/60 text-xs font-mono text-zinc-400 hover:text-rose-400 transition-all active:scale-95"
                title="Lock Admin Console"
              >
                Log Out
              </button>
            )}
          </div>
        </header>

        {/* LOCKED SCREEN IF NOT AUTHENTICATED */}
        {!isAdminLoggedIn ? (
          <div className="max-w-md mx-auto my-12 p-6 sm:p-8 bg-zinc-950 border border-zinc-800/80 rounded-2xl text-center shadow-2xl animate-scaleIn">
            <div className="w-12 h-12 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-amber-400 mx-auto mb-4 shadow-inner">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-lg sm:text-xl font-bold font-sans text-white mb-2">Admin Console</h1>
            <p className="text-xs text-zinc-400 font-mono mb-6 leading-relaxed">
              Enter your passcode to manage quotes, schedule dates, and configure anchor settings.
            </p>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <input
                  type="password"
                  required
                  placeholder="Enter admin passcode..."
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (loginError) setLoginError('');
                  }}
                  className="w-full px-4 py-3 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-sm font-mono focus:border-amber-400 focus:outline-none transition-colors"
                  autoFocus
                />
              </div>

              {loginError && (
                <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-900/60 text-xs text-rose-400 font-mono text-left animate-fadeIn">
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoggingIn || !password}
                className="w-full py-3 bg-white hover:bg-zinc-200 text-black font-semibold rounded-xl text-sm transition-all active:scale-98 disabled:opacity-40"
              >
                {isLoggingIn ? 'Authenticating...' : 'Unlock Console'}
              </button>
            </form>
          </div>
        ) : (
          /* UNLOCKED ADMIN PANEL */
          <div className="space-y-6 animate-fadeIn">

            {/* DASHBOARD METRICS CARDS (Interactive Quick Filters) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
              {/* Total Quotes */}
              <div
                onClick={() => handleMetricClick('all')}
                className={`p-3.5 sm:p-4 rounded-2xl border transition-all cursor-pointer ${
                  statusFilter === 'all' && activeTab === 'list'
                    ? 'bg-zinc-900 border-zinc-600 shadow-md'
                    : 'bg-zinc-950/80 border-zinc-800/80 hover:border-zinc-700'
                }`}
              >
                <div className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider mb-1 flex items-center justify-between">
                  <span>Total Quotes</span>
                  <span className="text-xs">📚</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-white">
                  {totalCount}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-1">In database</div>
              </div>

              {/* Active Quotes */}
              <div
                onClick={() => handleMetricClick('active')}
                className={`p-3.5 sm:p-4 rounded-2xl border transition-all cursor-pointer ${
                  statusFilter === 'active' && activeTab === 'list'
                    ? 'bg-emerald-950/40 border-emerald-500/60 shadow-md'
                    : 'bg-zinc-950/80 border-zinc-800/80 hover:border-emerald-800/60'
                }`}
              >
                <div className="text-[11px] font-mono text-emerald-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                  <span>Active</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-emerald-300">
                  {activeCount}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-1">Live in rotation</div>
              </div>

              {/* Disabled Quotes */}
              <div
                onClick={() => handleMetricClick('disabled')}
                className={`p-3.5 sm:p-4 rounded-2xl border transition-all cursor-pointer ${
                  statusFilter === 'disabled' && activeTab === 'list'
                    ? 'bg-rose-950/40 border-rose-600 shadow-md'
                    : 'bg-zinc-950/80 border-zinc-800/80 hover:border-rose-900/60'
                }`}
              >
                <div className="text-[11px] font-mono text-rose-400 uppercase tracking-wider mb-1 flex items-center justify-between">
                  <span>Disabled</span>
                  <span className="w-2 h-2 rounded-full bg-rose-400" />
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-rose-300">
                  {disabledCount}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-1">Hidden from public</div>
              </div>

              {/* Scheduled for Date */}
              <div
                onClick={() => handleMetricClick('scheduled')}
                className={`p-3.5 sm:p-4 rounded-2xl border transition-all cursor-pointer ${
                  statusFilter === 'scheduled' && activeTab === 'list'
                    ? 'bg-amber-950/40 border-amber-500/60 shadow-md'
                    : 'bg-zinc-950/80 border-zinc-800/80 hover:border-amber-900/60'
                }`}
              >
                <div className="text-[11px] font-mono text-amber-300 uppercase tracking-wider mb-1 flex items-center justify-between">
                  <span>Scheduled</span>
                  <span className="text-xs">📅</span>
                </div>
                <div className="text-xl sm:text-2xl font-bold font-mono text-amber-300">
                  {scheduledCount}
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-1">Date assigned</div>
              </div>
            </div>

            {/* RESPONSIVE SEGMENTED TABS */}
            <div className="flex overflow-x-auto p-1 bg-zinc-950/80 border border-zinc-800/80 rounded-2xl text-xs font-mono scrollbar-none">
              <button
                onClick={() => setActiveTab('list')}
                className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-center transition-all whitespace-nowrap flex items-center justify-center gap-1.5 ${
                  activeTab === 'list'
                    ? 'bg-zinc-800 text-white font-bold shadow'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <span>📚 Library</span>
                <span className="px-1.5 py-0.2 rounded-full bg-zinc-900 text-[10px] text-zinc-400">
                  {quotes.length}
                </span>
              </button>

              <button
                onClick={() => setActiveTab('schedule')}
                className={`flex-1 min-w-[120px] py-2.5 px-3 rounded-xl text-center transition-all whitespace-nowrap flex items-center justify-center gap-1.5 ${
                  activeTab === 'schedule'
                    ? 'bg-zinc-800 text-white font-bold shadow'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <span>📅 Date Schedule</span>
                {scheduledCount > 0 && (
                  <span className="px-1.5 py-0.2 rounded-full bg-amber-950/80 text-amber-300 text-[10px] border border-amber-900/50">
                    {scheduledCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('add')}
                className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-center transition-all whitespace-nowrap flex items-center justify-center gap-1.5 ${
                  activeTab === 'add'
                    ? 'bg-zinc-800 text-white font-bold shadow'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <span>➕ Add Quote</span>
              </button>

              <button
                onClick={() => setActiveTab('settings')}
                className={`flex-1 min-w-[100px] py-2.5 px-3 rounded-xl text-center transition-all whitespace-nowrap flex items-center justify-center gap-1.5 ${
                  activeTab === 'settings'
                    ? 'bg-zinc-800 text-white font-bold shadow'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                <span>⚙️ Settings</span>
              </button>
            </div>

            {/* ======================================================== */}
            {/* TAB 1: QUOTES LIBRARY (Search, Disable, Enable, Date)    */}
            {/* ======================================================== */}
            {activeTab === 'list' && (
              <div className="space-y-4">
                {/* Search & Filter Toolbar */}
                <div className="p-4 bg-zinc-950/80 border border-zinc-800/80 rounded-2xl space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2.5">
                    {/* Search Input */}
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        placeholder="Search quote text or author..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-3.5 pr-8 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400 transition-colors"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white text-xs font-mono"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {/* Category Filter */}
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="px-3 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400"
                    >
                      <option value="all">All Categories</option>
                      {existingCategories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>

                    {/* Date Specific Filter */}
                    <input
                      type="date"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value)}
                      className="px-3 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400"
                      title="Filter by assigned calendar date"
                    />

                    {filterDate && (
                      <button
                        onClick={() => setFilterDate('')}
                        className="px-3 py-2 rounded-xl bg-zinc-900 border border-zinc-800 text-xs font-mono text-zinc-400 hover:text-white"
                        title="Clear date filter"
                      >
                        Clear Date
                      </button>
                    )}
                  </div>

                  {/* Status Filter Chips */}
                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-zinc-850">
                    <div className="flex flex-wrap items-center gap-1.5 text-xs font-mono">
                      <span className="text-[11px] text-zinc-500 mr-1 uppercase">Filter:</span>
                      
                      <button
                        onClick={() => setStatusFilter('all')}
                        className={`px-2.5 py-1 rounded-lg transition-all ${
                          statusFilter === 'all'
                            ? 'bg-zinc-800 text-white font-bold'
                            : 'bg-zinc-900/60 text-zinc-400 hover:text-white'
                        }`}
                      >
                        All ({quotes.length})
                      </button>

                      <button
                        onClick={() => setStatusFilter('active')}
                        className={`px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 ${
                          statusFilter === 'active'
                            ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/80 font-bold'
                            : 'bg-zinc-900/60 text-zinc-400 hover:text-white'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Active ({activeCount})
                      </button>

                      <button
                        onClick={() => setStatusFilter('disabled')}
                        className={`px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 ${
                          statusFilter === 'disabled'
                            ? 'bg-rose-950/80 text-rose-300 border border-rose-800/80 font-bold'
                            : 'bg-zinc-900/60 text-zinc-400 hover:text-white'
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
                        Disabled ({disabledCount})
                      </button>

                      <button
                        onClick={() => setStatusFilter('scheduled')}
                        className={`px-2.5 py-1 rounded-lg transition-all flex items-center gap-1.5 ${
                          statusFilter === 'scheduled'
                            ? 'bg-amber-950/80 text-amber-300 border border-amber-800/80 font-bold'
                            : 'bg-zinc-900/60 text-zinc-400 hover:text-white'
                        }`}
                      >
                        <span>📅</span>
                        Scheduled ({scheduledCount})
                      </button>
                    </div>

                    <button
                      onClick={handleResetDefaults}
                      className="text-[11px] font-mono text-zinc-500 hover:text-zinc-300 underline transition-colors"
                      title="Restore original seed quotes"
                    >
                      Restore Seeds
                    </button>
                  </div>
                </div>

                {/* Quote Cards List */}
                <div className="space-y-3">
                  {filteredQuotes.length === 0 ? (
                    <div className="p-12 text-center bg-zinc-950/60 border border-zinc-800/60 rounded-2xl space-y-3">
                      <p className="text-sm font-mono text-zinc-400">No quotes match your filter criteria.</p>
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setStatusFilter('all');
                          setCategoryFilter('all');
                          setFilterDate('');
                        }}
                        className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-mono text-zinc-300 rounded-xl"
                      >
                        Clear All Filters
                      </button>
                    </div>
                  ) : (
                    filteredQuotes.map((q) => {
                      const isActive = q.is_active !== 0;
                      const hasDate = !!q.assigned_date;
                      const isToday = q.assigned_date === todayString;

                      return (
                        <div
                          key={q.id}
                          className={`p-4 sm:p-5 rounded-2xl border transition-all ${
                            !isActive
                              ? 'bg-zinc-950/40 border-zinc-900 opacity-70'
                              : isToday
                              ? 'bg-zinc-950/90 border-amber-500/50 shadow-lg'
                              : 'bg-zinc-950/80 border-zinc-800/80 hover:border-zinc-700'
                          }`}
                        >
                          {/* Card Header: Status Toggle, Date Tag, Category */}
                          <div className="flex flex-wrap items-center justify-between gap-2 pb-3 mb-3 border-b border-zinc-850/60">
                            {/* ACTIVE / DISABLED TOGGLE SWITCH */}
                            <div className="flex items-center gap-2.5">
                              <div
                                onClick={() => handleToggleActive(q.id, q.is_active)}
                                className={`toggle-switch-track ${isActive ? 'active' : ''}`}
                                role="switch"
                                aria-checked={isActive}
                                title={isActive ? 'Click to disable quote' : 'Click to enable quote'}
                              >
                                <div className="toggle-switch-thumb" />
                              </div>
                              <span
                                onClick={() => handleToggleActive(q.id, q.is_active)}
                                className={`text-xs font-mono cursor-pointer select-none font-semibold ${
                                  isActive ? 'text-emerald-400' : 'text-zinc-500'
                                }`}
                              >
                                {isActive ? 'Active' : 'Disabled'}
                              </span>
                            </div>

                            {/* Date Badge / Pill */}
                            <div className="flex items-center gap-2">
                              {hasDate ? (
                                <button
                                  onClick={() => openDateModal(q)}
                                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-mono border transition-all ${
                                    isToday
                                      ? 'bg-amber-400/20 text-amber-300 border-amber-400/50 font-bold'
                                      : 'bg-amber-950/40 text-amber-300 border-amber-900/50 hover:bg-amber-900/50'
                                  }`}
                                  title="Click to change or remove assigned date"
                                >
                                  <span>📅</span>
                                  <span>
                                    {getFriendlyDateLabel(q.assigned_date!)} ({q.assigned_date})
                                  </span>
                                </button>
                              ) : (
                                <button
                                  onClick={() => openDateModal(q)}
                                  className="px-2.5 py-1 rounded-lg bg-zinc-900 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-850 border border-zinc-800 text-[10px] font-mono transition-colors"
                                  title="Assign this quote to a specific date"
                                >
                                  + Assign Date
                                </button>
                              )}

                              <span className="px-2 py-0.5 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 text-[10px] font-mono">
                                {q.category}
                              </span>
                            </div>
                          </div>

                          {/* Quote Content */}
                          <div className="space-y-2">
                            <p className="text-sm sm:text-base font-serif italic text-zinc-200 leading-relaxed">
                              "{q.text}"
                            </p>
                            <p className="text-xs font-mono text-zinc-400">
                              — <span className="text-zinc-300 font-medium">{q.author}</span>
                            </p>
                          </div>

                          {/* Action Footer */}
                          <div className="flex flex-wrap items-center justify-between gap-2 pt-3 mt-3 border-t border-zinc-850/60">
                            <div className="flex items-center gap-2">
                              {/* Enable for Date / Schedule Button */}
                              <button
                                onClick={() => openDateModal(q)}
                                className="px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-mono text-amber-300 hover:text-amber-200 transition-all flex items-center gap-1.5 active:scale-95"
                              >
                                <span>📅</span>
                                <span>{hasDate ? 'Reschedule' : 'Enable for Date'}</span>
                              </button>

                              {/* Edit Button */}
                              <button
                                onClick={() => openEditModal(q)}
                                className="px-3 py-1.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-mono text-zinc-300 hover:text-white transition-all active:scale-95"
                              >
                                Edit
                              </button>
                            </div>

                            {/* Delete Button */}
                            <button
                              onClick={() => handleDeleteQuote(q.id)}
                              className="p-2 text-zinc-500 hover:text-rose-400 rounded-xl hover:bg-zinc-900 transition-colors"
                              title="Delete quote"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB 2: DATE SCHEDULE (Specialized Date & Quote Manager)  */}
            {/* ======================================================== */}
            {activeTab === 'schedule' && (
              <div className="space-y-6">
                {/* TODAY'S FEATURED QUOTE CARD */}
                <div className="p-5 sm:p-6 bg-zinc-950/90 border border-zinc-800/80 rounded-2xl space-y-4 shadow-xl">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-zinc-850">
                    <div>
                      <span className="text-[10px] font-mono text-amber-400 uppercase tracking-wider block">Calendar Focus</span>
                      <h2 className="text-base sm:text-lg font-bold text-white">
                        Today: {getFriendlyDateLabel(todayString)} ({todayString})
                      </h2>
                    </div>
                    <span className="text-xs font-mono text-zinc-400">
                      Calculated Day {previewStats.dayNumber}
                    </span>
                  </div>

                  {todayQuotes.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                        <span className="text-xs font-mono text-emerald-400 font-semibold">
                          Custom Quote Scheduled for Today:
                        </span>
                      </div>

                      {todayQuotes.map((q) => (
                        <div
                          key={q.id}
                          className="p-4 rounded-xl bg-amber-950/20 border border-amber-500/40 space-y-2"
                        >
                          <p className="text-sm sm:text-base font-serif italic text-amber-100">
                            "{q.text}"
                          </p>
                          <div className="flex items-center justify-between pt-2 text-xs font-mono">
                            <span className="text-amber-300">— {q.author}</span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => openDateModal(q)}
                                className="px-2.5 py-1 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 hover:text-white"
                              >
                                Change Date
                              </button>
                              <button
                                onClick={async () => {
                                  await assignQuoteDate(q.id, null);
                                  onQuotesUpdated(
                                    quotes.map((item) =>
                                      item.id === q.id ? { ...item, assigned_date: null } : item
                                    )
                                  );
                                  showToast('Removed from Today (returned to general pool)');
                                }}
                                className="px-2.5 py-1 rounded-lg bg-zinc-900 border border-zinc-800 text-rose-400 hover:text-rose-300"
                              >
                                Remove from Today
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-850 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-xs font-mono text-zinc-300 font-medium">
                          No specific quote is pinned for today.
                        </p>
                        <p className="text-[11px] font-mono text-zinc-500">
                          The public site is randomly serving active quotes from the general rotation pool.
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          // Filter quotes not yet assigned
                          const candidates = quotes.filter((q) => q.is_active !== 0 && !q.assigned_date);
                          if (candidates.length > 0) {
                            openDateModal(candidates[0]);
                          } else if (quotes.length > 0) {
                            openDateModal(quotes[0]);
                          } else {
                            setActiveTab('add');
                          }
                        }}
                        className="px-4 py-2.5 bg-amber-400 hover:bg-amber-300 text-black font-semibold rounded-xl text-xs font-mono transition-all whitespace-nowrap active:scale-95"
                      >
                        + Pin a Quote for Today
                      </button>
                    </div>
                  )}
                </div>

                {/* SCHEDULED DATES LIST */}
                <div className="p-5 sm:p-6 bg-zinc-950/80 border border-zinc-800/80 rounded-2xl space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-bold text-white">All Date-Scheduled Quotes</h3>
                      <p className="text-xs text-zinc-400 font-mono">
                        Quotes targeted for specific calendar dates ({allScheduledQuotes.length} scheduled)
                      </p>
                    </div>

                    <button
                      onClick={() => setActiveTab('add')}
                      className="px-3 py-2 bg-zinc-900 hover:bg-zinc-850 border border-zinc-800 text-zinc-300 text-xs font-mono rounded-xl"
                    >
                      + Create for Date
                    </button>
                  </div>

                  {allScheduledQuotes.length === 0 ? (
                    <p className="text-center text-xs font-mono text-zinc-500 py-8">
                      No quotes are currently assigned to specific dates.
                    </p>
                  ) : (
                    <div className="space-y-2.5">
                      {allScheduledQuotes.map((q) => {
                        const isPast = q.assigned_date! < todayString;
                        const isToday = q.assigned_date === todayString;

                        return (
                          <div
                            key={q.id}
                            className={`p-3.5 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                              isToday
                                ? 'bg-amber-950/20 border-amber-500/40'
                                : isPast
                                ? 'bg-zinc-950/40 border-zinc-900 opacity-60'
                                : 'bg-zinc-900/60 border-zinc-800'
                            }`}
                          >
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 rounded bg-amber-950/80 border border-amber-900/60 text-amber-300 text-[11px] font-mono font-bold">
                                  📅 {getFriendlyDateLabel(q.assigned_date!)} ({q.assigned_date})
                                </span>
                                {q.is_active === 0 && (
                                  <span className="px-1.5 py-0.2 rounded bg-rose-950 text-rose-400 text-[10px] font-mono">
                                    Disabled
                                  </span>
                                )}
                              </div>
                              <p className="text-xs font-serif italic text-zinc-200 line-clamp-1">
                                "{q.text}"
                              </p>
                              <p className="text-[11px] font-mono text-zinc-500">
                                — {q.author}
                              </p>
                            </div>

                            <div className="flex items-center gap-2 self-end sm:self-center">
                              <button
                                onClick={() => openDateModal(q)}
                                className="px-2.5 py-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-mono text-zinc-300"
                              >
                                Change Date
                              </button>
                              <button
                                onClick={async () => {
                                  await assignQuoteDate(q.id, null);
                                  onQuotesUpdated(
                                    quotes.map((item) =>
                                      item.id === q.id ? { ...item, assigned_date: null } : item
                                    )
                                  );
                                  showToast('Quote date cleared');
                                }}
                                className="p-1.5 text-zinc-500 hover:text-rose-400"
                                title="Remove assigned date"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB 3: ADD NEW QUOTE                                     */}
            {/* ======================================================== */}
            {activeTab === 'add' && (
              <div className="p-5 sm:p-7 bg-zinc-950/80 border border-zinc-800/80 rounded-2xl space-y-6">
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-white mb-1">Add New Quote</h2>
                  <p className="text-xs text-zinc-400 font-mono">
                    Create a new quote. Set a specific date to highlight it on that day, or leave date empty for general rotation.
                  </p>
                </div>

                <form onSubmit={handleCreateQuote} className="space-y-5">
                  {/* Quote Text */}
                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="text-xs font-mono text-zinc-400 uppercase">
                        Quote Text <span className="text-amber-400">*</span>
                      </label>
                      <span className="text-[10px] font-mono text-zinc-500">
                        {newText.length} characters
                      </span>
                    </div>
                    <textarea
                      rows={3}
                      required
                      placeholder="e.g. He who conquers himself is the mightiest warrior."
                      value={newText}
                      onChange={(e) => setNewText(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-sm font-serif focus:border-amber-400 resize-none transition-colors"
                    />
                  </div>

                  {/* Author with Quick Suggestions */}
                  <div className="space-y-2">
                    <label className="block text-xs font-mono text-zinc-400 uppercase">
                      Author / Source
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Marcus Aurelius"
                      value={newAuthor}
                      onChange={(e) => setNewAuthor(e.target.value)}
                      className="w-full px-3.5 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400 transition-colors"
                    />
                    {/* Quick Author Suggestion Chips */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <span className="text-[10px] font-mono text-zinc-500 self-center">Suggestions:</span>
                      {AUTHOR_SUGGESTIONS.map((authorName) => (
                        <button
                          key={authorName}
                          type="button"
                          onClick={() => setNewAuthor(authorName)}
                          className="px-2 py-0.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-850 text-[10px] font-mono text-zinc-400 hover:text-white transition-colors"
                        >
                          {authorName}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Grid for Category, Date Assignment, Status */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Category */}
                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1.5 uppercase">
                        Category / Theme
                      </label>
                      <select
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400"
                      >
                        {CATEGORY_OPTIONS.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    {/* Assigned Date with Shortcuts */}
                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <label className="text-xs font-mono text-amber-300 uppercase">
                          Specific Date (Optional)
                        </label>
                        {newAssignedDate && (
                          <button
                            type="button"
                            onClick={() => setNewAssignedDate('')}
                            className="text-[10px] font-mono text-zinc-500 hover:text-white"
                          >
                            Clear Date
                          </button>
                        )}
                      </div>
                      <input
                        type="date"
                        value={newAssignedDate}
                        onChange={(e) => setNewAssignedDate(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400"
                      />
                      {/* Date Quick Shortcuts */}
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <button
                          type="button"
                          onClick={() => setNewAssignedDate(todayString)}
                          className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-[10px] font-mono text-zinc-400 hover:text-amber-300"
                        >
                          Today
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewAssignedDate(tomorrowString)}
                          className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-[10px] font-mono text-zinc-400 hover:text-amber-300"
                        >
                          Tomorrow
                        </button>
                        <span className="text-[10px] font-mono text-zinc-600">
                          {newAssignedDate ? `Set: ${getFriendlyDateLabel(newAssignedDate)}` : 'General rotation'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Active Toggle for New Quote */}
                  <div className="p-3.5 rounded-xl bg-zinc-900/60 border border-zinc-850 flex items-center justify-between">
                    <div>
                      <span className="text-xs font-mono text-white font-medium block">
                        Enable immediately
                      </span>
                      <span className="text-[11px] font-mono text-zinc-500">
                        Quote will be active and available in rotation
                      </span>
                    </div>

                    <div
                      onClick={() => setNewIsActive(!newIsActive)}
                      className={`toggle-switch-track ${newIsActive ? 'active' : ''}`}
                      role="switch"
                      aria-checked={newIsActive}
                    >
                      <div className="toggle-switch-thumb" />
                    </div>
                  </div>

                  {/* Live Card Preview */}
                  {newText.trim() && (
                    <div className="p-4 rounded-xl bg-zinc-900/40 border border-zinc-850 space-y-1.5 animate-fadeIn">
                      <span className="text-[10px] font-mono text-zinc-500 uppercase block">Preview</span>
                      <p className="text-sm font-serif italic text-zinc-200">
                        "{newText}"
                      </p>
                      <p className="text-xs font-mono text-zinc-500">
                        — {newAuthor.trim() || 'Anonymous'}
                        {newAssignedDate && (
                          <span className="ml-2 text-amber-300">({newAssignedDate})</span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmittingQuote || !newText.trim()}
                    className="w-full sm:w-auto px-8 py-3 bg-white hover:bg-zinc-200 text-black font-semibold rounded-xl text-xs font-mono transition-all active:scale-95 disabled:opacity-40"
                  >
                    {isSubmittingQuote ? 'Saving Quote...' : 'Add Quote to Database'}
                  </button>
                </form>
              </div>
            )}

            {/* ======================================================== */}
            {/* TAB 4: SETTINGS & CONFIGURATION                          */}
            {/* ======================================================== */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                {/* Since Date Form */}
                <div className="p-5 sm:p-7 bg-zinc-950/80 border border-zinc-800/80 rounded-2xl space-y-5">
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-white mb-1">
                      Anchor "Since Date" Configuration
                    </h2>
                    <p className="text-xs text-zinc-400 font-mono">
                      The prominent Day number displayed on the main page calculates elapsed days relative to this calendar date.
                    </p>
                  </div>

                  <form onSubmit={handleSaveSinceDate} className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-xs font-mono text-zinc-400 mb-1.5 uppercase">
                        Since Date (YYYY-MM-DD)
                      </label>
                      <input
                        type="date"
                        required
                        value={sinceDateInput}
                        onChange={(e) => setSinceDateInput(e.target.value)}
                        className="w-full px-4 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-xl text-white text-sm font-mono focus:border-amber-400"
                      />
                    </div>

                    <div className="p-4 bg-zinc-900/60 border border-zinc-850 rounded-xl flex items-center justify-between">
                      <div>
                        <span className="text-[10px] font-mono text-zinc-500 uppercase block">Live Preview</span>
                        <span className="text-xl font-bold text-white font-mono">
                          Day {previewStats.dayNumber}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-zinc-400">
                        {previewStats.totalDaysElapsed} full days elapsed
                      </span>
                    </div>

                    <button
                      type="submit"
                      disabled={isSavingConfig}
                      className="px-6 py-2.5 bg-amber-400 hover:bg-amber-300 text-black font-semibold rounded-xl text-xs font-mono transition-all disabled:opacity-50 active:scale-95"
                    >
                      {isSavingConfig ? 'Saving...' : 'Save Since Date'}
                    </button>
                  </form>
                </div>

                {/* Cloudflare D1 Info */}
                <div className="p-5 sm:p-7 bg-zinc-950/80 border border-zinc-800/80 rounded-2xl space-y-4 text-xs font-mono">
                  <div className="flex items-center justify-between pb-3 border-b border-zinc-850">
                    <span className="text-zinc-400">Database Binding Status:</span>
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                      isD1 
                        ? 'bg-emerald-950 text-emerald-300 border border-emerald-800' 
                        : 'bg-amber-950 text-amber-300 border border-amber-800'
                    }`}>
                      {isD1 ? '● Cloudflare D1 Connected' : '○ Standalone / Local Storage Mode'}
                    </span>
                  </div>

                  <p className="text-zinc-400 leading-relaxed text-[11px]">
                    When deployed on Cloudflare Pages, quote entries and the anchor since date are saved directly into your edge SQLite D1 database.
                  </p>

                  <div className="pt-2">
                    <button
                      onClick={handleResetDefaults}
                      className="px-4 py-2 bg-zinc-900 hover:bg-rose-950/50 border border-zinc-800 hover:border-rose-900/50 text-zinc-400 hover:text-rose-400 rounded-xl text-xs font-mono transition-colors"
                    >
                      Reset & Re-seed Default Quotes
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

      </div>

      {/* ======================================================== */}
      {/* MODAL: ENABLE FOR CERTAIN DATE / SCHEDULE DATE           */}
      {/* ======================================================== */}
      {dateModalQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-2xl p-5 sm:p-6 shadow-2xl space-y-4 animate-scaleIn">
            <div className="flex items-center justify-between pb-2 border-b border-zinc-850">
              <div className="flex items-center gap-2">
                <span className="text-base">📅</span>
                <h3 className="text-sm sm:text-base font-bold text-white font-sans">
                  Schedule Quote for Date
                </h3>
              </div>
              <button
                onClick={() => setDateModalQuote(null)}
                className="p-1 text-zinc-400 hover:text-white text-sm"
              >
                ✕
              </button>
            </div>

            {/* Target Quote Preview */}
            <div className="p-3 bg-zinc-900/60 border border-zinc-850 rounded-xl space-y-1">
              <p className="text-xs font-serif italic text-zinc-200 line-clamp-2">
                "{dateModalQuote.text}"
              </p>
              <p className="text-[11px] font-mono text-zinc-400">
                — {dateModalQuote.author}
              </p>
            </div>

            {/* Date Input */}
            <div className="space-y-2">
              <label className="block text-xs font-mono text-zinc-400 uppercase">
                Select Calendar Date (YYYY-MM-DD)
              </label>
              <input
                type="date"
                value={dateModalInput}
                onChange={(e) => setDateModalInput(e.target.value)}
                className="w-full px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm font-mono focus:border-amber-400"
              />

              {/* Quick Preset Buttons */}
              <div className="flex flex-wrap gap-1.5 pt-1">
                <button
                  type="button"
                  onClick={() => setDateModalInput(todayString)}
                  className="px-2.5 py-1 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-[11px] font-mono text-amber-300"
                >
                  Today ({todayString})
                </button>
                <button
                  type="button"
                  onClick={() => setDateModalInput(tomorrowString)}
                  className="px-2.5 py-1 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-[11px] font-mono text-zinc-300"
                >
                  Tomorrow
                </button>
                <button
                  type="button"
                  onClick={() => setDateModalInput('')}
                  className="px-2.5 py-1 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-[11px] font-mono text-rose-400"
                >
                  Clear Date (General Rotation)
                </button>
              </div>
            </div>

            <div className="p-3 rounded-xl bg-emerald-950/30 border border-emerald-900/50 text-[11px] font-mono text-emerald-300">
              ✓ Scheduling this quote will automatically enable it and feature it on the selected date.
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDateModalQuote(null)}
                className="px-4 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-mono text-zinc-400"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveDateModal}
                className="px-5 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-semibold text-xs font-mono transition-all active:scale-95"
              >
                Apply & Enable for Date
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: EDIT QUOTE DETAILS                                */}
      {/* ======================================================== */}
      {editingQuote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-2xl p-5 sm:p-6 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto animate-scaleIn">
            <div className="flex items-center justify-between pb-2 border-b border-zinc-850">
              <h3 className="text-base font-bold text-white font-sans">
                Edit Quote
              </h3>
              <button
                onClick={() => setEditingQuote(null)}
                className="p-1 text-zinc-400 hover:text-white text-sm"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              {/* Text */}
              <div>
                <label className="block text-xs font-mono text-zinc-400 mb-1.5 uppercase">
                  Quote Text *
                </label>
                <textarea
                  rows={3}
                  required
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-sm font-serif focus:border-amber-400 resize-none"
                />
              </div>

              {/* Author & Category */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1.5 uppercase">
                    Author
                  </label>
                  <input
                    type="text"
                    value={editAuthor}
                    onChange={(e) => setEditAuthor(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400"
                  />
                </div>

                <div>
                  <label className="block text-xs font-mono text-zinc-400 mb-1.5 uppercase">
                    Category
                  </label>
                  <input
                    type="text"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400"
                  />
                </div>
              </div>

              {/* Assigned Date */}
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-mono text-amber-300 uppercase">
                    Assigned Date (Optional)
                  </label>
                  {editAssignedDate && (
                    <button
                      type="button"
                      onClick={() => setEditAssignedDate('')}
                      className="text-[10px] font-mono text-zinc-500 hover:text-white"
                    >
                      Clear Date
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  value={editAssignedDate}
                  onChange={(e) => setEditAssignedDate(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-xl text-white text-xs font-mono focus:border-amber-400"
                />
              </div>

              {/* Active Toggle */}
              <div className="p-3 rounded-xl bg-zinc-900/60 border border-zinc-850 flex items-center justify-between">
                <div>
                  <span className="text-xs font-mono text-white font-medium block">
                    Active Status
                  </span>
                  <span className="text-[11px] font-mono text-zinc-500">
                    {editIsActive ? 'Visible to visitors' : 'Hidden from public site'}
                  </span>
                </div>

                <div
                  onClick={() => setEditIsActive(!editIsActive)}
                  className={`toggle-switch-track ${editIsActive ? 'active' : ''}`}
                  role="switch"
                  aria-checked={editIsActive}
                >
                  <div className="toggle-switch-thumb" />
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setEditingQuote(null)}
                  className="px-4 py-2 rounded-xl bg-zinc-900 border border-zinc-800 text-xs font-mono text-zinc-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSavingEdit || !editText.trim()}
                  className="px-5 py-2 rounded-xl bg-white hover:bg-zinc-200 text-black font-semibold text-xs font-mono transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSavingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
